import React, { forwardRef } from 'react'
import { Box, Flex, Text, SVG } from './Base'
import { getMarginProps, omitMarginProps } from '@utils/helpers'

export const FormLabel = forwardRef((props, ref) => (
    <Text
        ref={ref}
        as="label"
        tx="forms"
        variant="label"
        {...props}
        __css={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            fontWeight: 'bold',
            fontSize: 'sm',
            cursor: 'pointer',
        }}
    />
))

export const FormError = forwardRef((props, ref) => (
    <Text ref={ref} tx="forms" variant="error" {...props} />
))

export const FormInput = forwardRef((props, ref) => (
    <Box
        ref={ref}
        as="input"
        type="text"
        tx="forms"
        variant="input"
        {...props}
        __css={{
            display: 'block',
            width: '100%',
            p: 2,
            appearance: 'none',
            fontSize: 'inherit',
            lineHeight: 'inherit',
            border: '1px solid',
            borderColor: 'neutral.300',
            boxShadow: 'input',
            color: 'inherit',
            bg: 'white',
        }}
    />
))

export const FormTextarea = forwardRef((props, ref) => (
    <Box
        ref={ref}
        as="textarea"
        tx="forms"
        variant="textarea"
        {...props}
        __css={{
            display: 'block',
            width: '100%',
            p: 2,
            appearance: 'none',
            fontSize: 'inherit',
            lineHeight: 'inherit',
            border: '1px solid',
            borderColor: 'neutral.300',
            boxShadow: 'input',
            color: 'inherit',
            bg: 'transparent',
        }}
    />
))

const FormSelectArrow = props => (
    <SVG {...props}>
        <path d="M5.293 6.95L6 7.657 11.657 2 10.243.586 6 4.828 1.757.586.343 2z" />
    </SVG>
)

export const FormSelect = forwardRef((props, ref) => (
    <Flex position="relative" {...getMarginProps(props)}>
        <Box
            ref={ref}
            as="select"
            tx="forms"
            variant="select"
            {...omitMarginProps(props)}
            __css={{
                display: 'block',
                width: '100%',
                p: 2,
                pr: 8,
                appearance: 'none',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                border: '1px solid',
                borderColor: 'neutral.300',
                borderRadius: 0,
                boxShadow: 'input',
                color: 'inherit',
                bg: 'transparent',
            }}
        />
        <FormSelectArrow
            width="12px"
            height="8px"
            sx={{
                position: 'absolute',
                right: 2,
                alignSelf: 'center',
                pointerEvents: 'none',
            }}
        />
    </Flex>
))

// const FormRadioChecked = props => (
//     <SVG {...props}>
//         <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
//     </SVG>
// )

// const FormRadioUnchecked = props => (
//     <SVG {...props}>
//         <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
//     </SVG>
// )

// const FormRadioIcon = props => (
//     <>
//         <FormRadioChecked
//             {...props}
//             __css={{
//                 display: 'none',
//                 'input:checked ~ &': {
//                     display: 'block',
//                 },
//             }}
//         />
//         <FormRadioUnchecked
//             {...props}
//             __css={{
//                 display: 'block',
//                 'input:checked ~ &': {
//                     display: 'none',
//                 },
//             }}
//         />
//     </>
// )

const FormRadioIcon = props => (
    <Flex
        justifyContent="center"
        alignItems="center"
        width={4}
        height={4}
        border="1px solid"
        borderColor="neutral.400"
        borderRadius="full"
        boxShadow="input"
        {...props}
    >
        <SVG
            display="none"
            color="primary"
            width="8px"
            height="8px"
            viewBox="0 0 16 16"
        >
            <circle cx="8" cy="8" r="8" />
        </SVG>
    </Flex>
)

export const FormRadio = forwardRef(
    ({ className, sx, variant = 'radio', ...props }, ref) => (
        <Box>
            <Box
                ref={ref}
                as="input"
                type="radio"
                {...props}
                sx={{
                    position: 'absolute',
                    overflow: 'hidden',
                    width: 1,
                    height: 1,
                    opacity: 0,
                    zIndex: -1,
                }}
            />
            <Box
                as={FormRadioIcon}
                aria-hidden="true"
                tx="forms"
                variant={variant}
                className={className}
                sx={sx}
                // {...getSystemProps(props)}
                __css={{
                    mr: 2,
                    'input:checked ~ & svg': {
                        display: 'block',
                    },
                    'input:focus ~ &': {
                        outline: 'auto 5px -webkit-focus-ring-color',
                    },
                }}
            />
        </Box>
    )
)

const FormCheckboxIcon = props => (
    <Flex
        justifyContent="center"
        alignItems="center"
        width={4}
        height={4}
        border="1px solid"
        borderColor="neutral.400"
        boxShadow="input"
        {...props}
    >
        <SVG
            display="none"
            color="primary"
            width="14px"
            height="14px"
            viewBox="0 0 20 20"
        >
            <path d="M0 11l2-2 5 5L18 3l2 2L7 18z" />
        </SVG>
    </Flex>
)

export const FormCheckbox = forwardRef(
    ({ className, sx, variant = 'checkbox', ...props }, ref) => (
        <Box>
            <Box
                ref={ref}
                as="input"
                type="checkbox"
                {...props}
                sx={{
                    overflow: 'hidden',
                    position: 'absolute',
                    opacity: 0,
                    zIndex: -1,
                    width: 1,
                    height: 1,
                }}
            />
            <Box
                as={FormCheckboxIcon}
                aria-hidden="true"
                tx="forms"
                variant={variant}
                className={className}
                sx={sx}
                // {...getSystemProps(props)}
                __css={{
                    mr: 2,
                    'input:checked ~ & svg': {
                        display: 'block',
                    },
                    'input:focus ~ &': {
                        outline: 'auto 5px -webkit-focus-ring-color',
                    },
                }}
            />
        </Box>
    )
)

export const FormSwitch = forwardRef(({ checked, ...props }, ref) => {
    const switchSize = 6

    return (
        <Box
            ref={ref}
            as="button"
            type="button"
            role="switch"
            tx="forms"
            variant="switch"
            aria-checked={checked}
            {...props}
            __css={{
                appearance: 'none',
                m: 0,
                p: 0,
                width: switchSize * 2,
                height: switchSize,
                color: 'neutral.400',
                bg: 'transparent',
                border: '1px solid',
                borderColor: 'neutral.600',
                borderRadius: 'full',
                // '&[aria-checked=true]': {
                //     bg: 'neutral.200',
                // },
                ':focus': {
                    outline: 'none',
                    boxShadow: '0 0 0 2px',
                },
            }}
        >
            <Box
                aria-hidden
                style={{
                    transform: checked
                        ? `translateX(${switchSize * 4}px)`
                        : 'translateX(0)',
                }}
                sx={{
                    mt: '-1px',
                    ml: '-1px',
                    width: switchSize,
                    height: switchSize,
                    borderRadius: 'full',
                    border: '1px solid',
                    borderColor: 'neutral.600',
                    bg: checked ? 'neutral.400' : 'neutral.200',
                    transitionProperty: 'transform',
                    transitionTimingFunction: 'ease-out',
                    transitionDuration: '0.1s',
                }}
            />
        </Box>
    )
})

export const FormSlider = forwardRef(({ ...props }, ref) => (
    <Box
        ref={ref}
        as="input"
        type="range"
        tx="forms"
        variant="slider"
        {...props}
        __css={{
            display: 'block',
            width: '100%',
            height: '2px',
            my: 3,
            cursor: 'pointer',
            appearance: 'none',
            borderRadius: 'full',
            color: 'neutral.500',
            bg: 'neutral.300',
            ':focus': {
                outline: 'none',
                color: 'neutral.600',
            },
            '&::-webkit-slider-thumb': {
                appearance: 'none',
                width: 6,
                height: 6,
                bg: 'currentcolor',
                border: '1px solid',
                borderColor: 'neutral.600',
                borderRadius: 'full',
            },
        }}
    />
))

export const FormHoneypot = forwardRef((props, ref) => (
    <Box
        ref={ref}
        as="input"
        name="your_company_website_url"
        tabindex="-1"
        autocomplete="off"
        {...props}
        __css={{
            display: 'none',
        }}
    />
))
